import { createReducer, on } from '@ngrx/store';
import {
  IpExclusion,
  IpExclusionsListMeta,
  IpExclusionStatistics,
} from '../models/ip-exclusion.model';
import * as IpExclusionsActions from './ip-exclusions.actions';
import { ApiTableFilter } from '../../../shared/models/table-filter.model';

export interface IpExclusionsQueryState {
  search?: string;
  filters?: ApiTableFilter[];
  filterLink?: string;
  sort?: string;
  sortDirection?: string;
}

export interface IpExclusionsState {
  ipExclusions: IpExclusion[];
  meta: IpExclusionsListMeta | null;
  statistics: IpExclusionStatistics | null;
  singleIpExclusion: IpExclusion | null;
  /** uuid of an existing exclusion when a create was rejected as a duplicate. */
  duplicateUuid: string | null;
  isLoading: boolean;
  isSubmitting: boolean;
  isDeleting: boolean;
  isBulkDeleting: boolean;
  isImporting: boolean;
  isLoadingSingle: boolean;
  error: string | null;
  lastQuery: IpExclusionsQueryState;
}

const initialState: IpExclusionsState = {
  ipExclusions: [],
  meta: null,
  statistics: null,
  singleIpExclusion: null,
  duplicateUuid: null,
  isLoading: false,
  isSubmitting: false,
  isDeleting: false,
  isBulkDeleting: false,
  isImporting: false,
  isLoadingSingle: false,
  error: null,
  lastQuery: {},
};

export const ipExclusionsReducer = createReducer(
  initialState,

  // ---- List ----
  on(IpExclusionsActions.loadIpExclusions, (state, action) => ({
    ...state,
    isLoading: true,
    error: null,
    lastQuery: {
      search: action.search,
      filters: action.filters,
      filterLink: action.filterLink,
      sort: action.sort,
      sortDirection: action.sortDirection,
    },
  })),
  on(IpExclusionsActions.loadIpExclusionsSuccess, (state, { ipExclusions, meta }) => ({
    ...state,
    ipExclusions,
    meta,
    isLoading: false,
  })),
  on(IpExclusionsActions.loadIpExclusionsFailure, (state, { error }) => ({
    ...state,
    isLoading: false,
    error,
  })),

  // ---- Statistics ----
  on(IpExclusionsActions.loadIpExclusionStatisticsSuccess, (state, { statistics }) => ({
    ...state,
    statistics,
  })),

  // ---- Submit ----
  on(IpExclusionsActions.submitIpExclusion, (state) => ({
    ...state,
    isSubmitting: true,
    duplicateUuid: null,
    error: null,
  })),
  on(IpExclusionsActions.submitIpExclusionSuccess, (state) => ({
    ...state,
    isSubmitting: false,
  })),
  on(IpExclusionsActions.submitIpExclusionDuplicate, (state, { duplicate }) => ({
    ...state,
    isSubmitting: false,
    duplicateUuid: duplicate,
  })),
  on(IpExclusionsActions.submitIpExclusionFailure, (state, { error }) => ({
    ...state,
    isSubmitting: false,
    error,
  })),

  // ---- Delete ----
  on(IpExclusionsActions.deleteIpExclusion, (state) => ({
    ...state,
    isDeleting: true,
    error: null,
  })),
  on(IpExclusionsActions.deleteIpExclusionSuccess, (state) => ({
    ...state,
    isDeleting: false,
  })),
  on(IpExclusionsActions.deleteIpExclusionFailure, (state, { error }) => ({
    ...state,
    isDeleting: false,
    error,
  })),

  // ---- Bulk delete ----
  on(IpExclusionsActions.bulkDeleteIpExclusions, (state) => ({
    ...state,
    isBulkDeleting: true,
    error: null,
  })),
  on(IpExclusionsActions.bulkDeleteIpExclusionsSuccess, (state) => ({
    ...state,
    isBulkDeleting: false,
  })),
  on(IpExclusionsActions.bulkDeleteIpExclusionsFailure, (state, { error }) => ({
    ...state,
    isBulkDeleting: false,
    error,
  })),

  // ---- Get by id ----
  on(IpExclusionsActions.loadIpExclusionById, (state) => ({
    ...state,
    isLoadingSingle: true,
    error: null,
  })),
  on(IpExclusionsActions.loadIpExclusionByIdSuccess, (state, { ipExclusion }) => ({
    ...state,
    singleIpExclusion: ipExclusion,
    isLoadingSingle: false,
  })),
  on(IpExclusionsActions.loadIpExclusionByIdFailure, (state, { error }) => ({
    ...state,
    isLoadingSingle: false,
    error,
  })),
  on(IpExclusionsActions.clearSingleIpExclusion, (state) => ({
    ...state,
    singleIpExclusion: null,
    duplicateUuid: null,
  })),

  // ---- Import ----
  on(IpExclusionsActions.importIpExclusions, (state) => ({
    ...state,
    isImporting: true,
    error: null,
  })),
  on(IpExclusionsActions.importIpExclusionsSuccess, (state) => ({
    ...state,
    isImporting: false,
  })),
  on(IpExclusionsActions.importIpExclusionsFailure, (state, { error }) => ({
    ...state,
    isImporting: false,
    error,
  })),

  on(IpExclusionsActions.resetIpExclusionsState, () => initialState),
);
